import React, {useEffect, useState, useRef} from 'react';
import {useSelector} from 'react-redux';
import AdminDetailListItem from './components/AdminDetailListItem';
import styles from './styles';
import {Card, Skeleton, Tag, theme, Typography} from 'antd';
import {rightPanelData} from './item';
import {getAddressGridData} from './AddressListGridDataSource';
import TableView from 'Components/TableView';
import {sortTable} from 'Utils/helpers/SRListHelpers';
import {SortOrder} from 'Utils/enums';
import {gqlGetMemberAddress} from 'GQL/MethodsBFF/ServiceRequest';
import {v4 as uuidv4} from 'uuid';
import NoResults from 'Components/NoResults';
import SnackBar from 'Components/SnackBar';

const PAGE_SIZE = 10;

// Headline fields pulled out into the mockup's `.stat-tile` row (the rest of
// rightPanelData still renders in the dense grid below, same as before).
const STAT_TILE_KEYS = ['externalRefId', 'memberNumber', 'status', 'memberType'];

// Free-form status string — pragmatic keyword heuristic for the badge color,
// same approach as the Service Requests list badges.
const statusTagColor = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (['cancel', 'suspend', 'inactive', 'block'].some((k) => s.includes(k))) return 'error';
  if (['pending', 'hold'].some((k) => s.includes(k))) return 'warning';
  if (['active', 'approved', 'confirmed'].some((k) => s.includes(k))) return 'success';
  return 'default';
};

const TabAdminProfile = () => {
  const {token}: any = theme.useToken();
  const member = useSelector((state: any) => state?.selectedMember?.selectedMember);
  const tenantConfig = useSelector((state: any) => state?.login?.tenantConfig);
  const loginDetails = useSelector((state: any) => state?.login);

  const [tableData, setTableData] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState(SortOrder.DESCENDING);
  const [currentSortCriteria, setCriteriaSort] = useState('endDate');
  const [pageNo, setPageNo] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const tableRef = useRef<HTMLDivElement | null>(null);

  const fetchMemberAddress = async (page: number) => {
    if (!member?.currentMemberDetails?.userId || loading) return;

    setLoading(true);
    setError(null);

    const headers = {
      'tenant-id': loginDetails?.tenantID,
      'x-correlation-id': uuidv4(),
      'user-access-token': loginDetails?.loginInfo?.access_token,
      'client-access-token': loginDetails?.loginInfo?.service_token,
    };

    const payload = {
      userId: member?.currentMemberDetails?.userId,
      pageSize: PAGE_SIZE.toString(),
      pageNumber: page.toString(),
      sortOrder: '',
      sortBy: '',
    };

    try {
      const response = await gqlGetMemberAddress(payload, headers);
      const addresses = response?.data?.getMemberAddress?.previousAddressDtoApis || [];
      const isNextPagePresent =
        response?.data?.getMemberAddress?.pagingOutputDto?.isNextPagePresent;

      setTableData((prev) => (page === 0 ? addresses : [...prev, ...addresses]));
      //setHasMore(addresses.length === PAGE_SIZE);
      setHasMore(isNextPagePresent);
    } catch (err) {
      console.error('Error fetching member addresses:', err);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (member && initialLoad) {
      fetchMemberAddress(0);
      setInitialLoad(false);
    }
  }, [member]);

  const nextPage = () => {
    if (!hasMore || loading) return;
    fetchMemberAddress(pageNo + 1);
    setPageNo((prev) => prev + 1);
  };

  const onSortIconClick = (sortCriteria: string) => {
    const sortedData = sortTable(
      tableData,
      sortCriteria,
      currentSortCriteria,
      sortOrder,
      tenantConfig.locale,
    );
    setCriteriaSort(sortedData.currentSortCriteria);
    setTableData(sortedData.tableData || []);
    setSortOrder(sortedData.newSortOrder);
  };

  const handleScroll = () => {
    if (!tableRef.current) return;
    const {scrollTop, scrollHeight, clientHeight} = tableRef.current;

    // Check if the user is close to the bottom (within 20px of the end)
    if (scrollTop + clientHeight >= scrollHeight - 20 && hasMore && !loading) {
      const prevHeight = scrollHeight; // Store previous scroll height before fetching new data
      nextPage();

      // Wait for data to be appended, then smoothly scroll down
      setTimeout(() => {
        if (tableRef.current) {
          const newHeight = tableRef.current.scrollHeight;
          tableRef.current.scrollTo({
            top: scrollTop + (newHeight - prevHeight), // Adjust scroll to maintain position
            behavior: 'smooth',
          });
        }
      }, 300); // Adjust delay based on fetch time
    }
  };

  const statTileFields = rightPanelData.filter((item) => STAT_TILE_KEYS.includes(item.key));
  const restFields = rightPanelData.filter((item) => !STAT_TILE_KEYS.includes(item.key));

  return (
    <div>
      <div style={styles.statTileGrid}>
        {statTileFields.map((item) => {
          const data = member?.currentMemberDetails?.[item?.key] || '';
          return (
            <Card key={item.key} styles={{body: {padding: '16px 18px'}}} bordered>
              <Typography.Text
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  color: token.colorTextTertiary,
                }}>
                {item.value}
              </Typography.Text>
              <div style={{marginTop: 6}}>
                {item.key === 'status' ? (
                  <Tag color={statusTagColor(data)}>{data || '-'}</Tag>
                ) : (
                  <Typography.Text style={{fontSize: 15, fontWeight: 600}}>
                    {data || '-'}
                  </Typography.Text>
                )}
              </div>
            </Card>
          );
        })}
      </div>
      <Card style={styles.paper} styles={{body: {padding: 16}}}>
        <div className="ast-admin-profile-grid">
          {restFields.map((item) => {
            let data = member?.currentMemberDetails?.[item?.key] || '';
            return (
              <div key={item.key} style={styles.gridItem}>
                <AdminDetailListItem label={item.value} value={data} type={item?.type} boldValue />
              </div>
            );
          })}
        </div>
        <style>{`
          .ast-admin-profile-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            max-height: 100%;
            gap: 16px;
            align-items: start;
          }
          @media (max-width: 1300px) {
            .ast-admin-profile-grid { grid-template-columns: repeat(3, 1fr); }
          }
        `}</style>
      </Card>

      <Typography.Title level={4} style={{marginTop: 28, marginBottom: 12}}>
        Address history
      </Typography.Title>
      {!loading && !error && tableData.length === 0 ? (
        <NoResults text="No addresses found." />
      ) : (
        <div
          ref={tableRef}
          style={{
            // No border/radius here — TableView now wraps itself in the
            // bordered `.table-wrap` container, so adding one here too would
            // double up. This div is purely the scroll region for pagination.
            maxHeight: 500,
            overflowY: 'auto',
            position: 'relative',
          }}
          onScroll={handleScroll}>
          {loading && pageNo === 0 && <Skeleton active paragraph={{rows: 6}} />}
          {error && (
            <SnackBar
              errorMessage={error}
              isSnackBarOpen={!!error}
              closeSnackBar={() => setError(null)}
            />
          )}

          {!loading && !error && tableData.length > 0 && (
            <div style={{position: 'relative'}}>
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  background: token.colorBgContainer,
                  zIndex: 100,
                  boxShadow: token.boxShadowTertiary,
                }}>
                <TableView
                  {...getAddressGridData(tableData, sortOrder, currentSortCriteria, true)}
                  onRowClick={() => {}}
                  sortTable={onSortIconClick}
                />
              </div>
            </div>
          )}

          {loading && (
            <div style={{textAlign: 'center', padding: 10}}>
              <Skeleton active title={false} paragraph={{rows: 1}} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TabAdminProfile;
